import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import MediaLibrary from './MediaLibrary';
import MenuSelector from './MenuSelector';
import type { RootState } from '../../redux/store';
import { fetchMenuItemsRequest } from '../../redux/slices/menuItemsSlice';

interface CreateOwnFormProps {
  onSubmit: (data: any) => void;
  initialData?: {
    eventDate?: string;
    title?: string;
    promotionType?: string;
    offerDetails?: string;
    Instructions?: string;
    tagLine?: string;
  };
}

const CreateOwnForm: React.FC<CreateOwnFormProps> = ({ onSubmit, initialData }) => {
  const dispatch = useDispatch();
  const { items: menuItems, loading: menuLoading } = useSelector((state: RootState) => state.menuItems);
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([]);
  const [selectedComboIds, setSelectedComboIds] = useState<string[]>([]);
  
  const [formData, setFormData] = useState({
    eventDate: initialData?.eventDate || '',
    title: initialData?.title || '',
    promotionType: initialData?.promotionType || '',
    offerDetails: initialData?.offerDetails || '',
    Instructions: initialData?.Instructions || '',
    tagLine: initialData?.tagLine || '',
    contactInfo: '',
    validFrom: '',
    validTo: '',
    themeColour: '#EE4423',
    menuItems: [] as Array<{ items: string }>,
    refImage: [] as string[],
    combo: [] as Array<{ combo: string }>
  });

  // Fetch menu items on mount
  useEffect(() => {
    if (menuItems.length === 0 && restaurantId) {
      dispatch(fetchMenuItemsRequest({ restaurantId }));
    }
  }, [dispatch, menuItems.length, restaurantId]);

  // Update formData when menu selections change
  useEffect(() => {
    const selectedMenuItems = menuItems.filter(item => selectedMenuIds.includes(item.pk_id));
    setFormData(prev => ({
      ...prev,
      menuItems: selectedMenuItems.map(item => ({ items: item.name }))
    }));
  }, [selectedMenuIds, menuItems]);

  useEffect(() => {
    const selectedComboItems = menuItems.filter(item => selectedComboIds.includes(item.pk_id));
    setFormData(prev => ({
      ...prev,
      combo: selectedComboItems.map(item => ({ combo: item.name }))
    }));
  }, [selectedComboIds, menuItems]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleSelectMedia = useCallback((url: string, type: 'upload' | 'pexels') => {
    setFormData(prev => ({
      ...prev,
      refImage: [...prev.refImage, url]
    }));
    // Close the media library after selecting an image
    setShowMediaLibrary(false);
  }, []);

  const removeRefImage = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      refImage: prev.refImage.filter((_, i) => i !== index)
    }));
  }, []);

  const removeMenuItem = (index: number) => {
    const menuItem = formData.menuItems[index];
    const itemToRemove = menuItems.find(item => item.name === menuItem.items);
    if (itemToRemove) {
      setSelectedMenuIds(prev => prev.filter(id => id !== itemToRemove.pk_id));
    }
  };

  const removeCombo = (index: number) => {
    const comboItem = formData.combo[index];
    const itemToRemove = menuItems.find(item => item.name === comboItem.combo);
    if (itemToRemove) {
      setSelectedComboIds(prev => prev.filter(id => id !== itemToRemove.pk_id));
    }
  };

  return (
    <>
      <motion.form
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        className="px-8 py-6"
      >
        {/* Main 7:3 Layout */}
        <div className="grid grid-cols-10 gap-6">
          {/* Left Column - All Form Fields (70%) */}
          <div className="col-span-7">
            <div className="grid grid-cols-2 gap-4">
              {/* Campaign Title */}
              <div className="c">
              <label className="block text-white text-sm font-medium mb-2">
                Campaign Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter campaign title"
                className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423] transition-colors"
                required
              />
            </div>

            {/* Offer Details */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Offer Details <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.offerDetails}
                onChange={(e) => setFormData({ ...formData, offerDetails: e.target.value })}
                placeholder="Describe the offer details"
                rows={2}
                className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423] transition-colors resize-none"
                required
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Instructions
              </label>
              <textarea
                value={formData.Instructions}
                onChange={(e) => setFormData({ ...formData, Instructions: e.target.value })}
                placeholder="Any additional information or context"
                rows={2}
                className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423] transition-colors resize-none"
              />
            </div>

            {/* Tag Line */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Tag Line
              </label>
              <input
                type="text"
                value={formData.tagLine}
                onChange={(e) => setFormData({ ...formData, tagLine: e.target.value })}
                placeholder="Enter catchy tagline"
                className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423] transition-colors"
              />
            </div>

            {/* Contact Information */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Contact Information
              </label>
              <input
                type="text"
                value={formData.contactInfo}
                onChange={(e) => setFormData({ ...formData, contactInfo: e.target.value })}
                placeholder="Phone, email, or website"
                className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423] transition-colors"
              />
            </div>

            {/* Valid From */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Valid From <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-[#EE4423] transition-colors"
                required
              />
            </div>

            {/* Valid To */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Valid To <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.validTo}
                onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                className="w-full px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-[#EE4423] transition-colors"
                required
              />
            </div>

            {/* Menu Items */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Menu Items
              </label>
              <MenuSelector
                items={menuItems}
                selectedItems={selectedMenuIds}
                onSelectionChange={setSelectedMenuIds}
                loading={menuLoading}
              />
              {formData.menuItems.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.menuItems.map((item, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 bg-[#EE4423]/10 border border-[#EE4423]/30 rounded-lg text-white flex items-center gap-2"
                    >
                      <span className="text-sm">{item.items}</span>
                      <button
                        type="button"
                        onClick={() => removeMenuItem(index)}
                        className="hover:bg-[#EE4423]/20 rounded-full p-1 transition-colors"
                      >
                        <MdClose size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Combo Offers */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Combo Offers
              </label>
              <MenuSelector
                items={menuItems}
                selectedItems={selectedComboIds}
                onSelectionChange={setSelectedComboIds}
                loading={menuLoading}
              />
              {formData.combo.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.combo.map((item, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 bg-[#EE4423]/10 border border-[#EE4423]/30 rounded-lg text-white flex items-center gap-2"
                    >
                      <span className="text-sm">{item.combo}</span>
                      <button
                        type="button"
                        onClick={() => removeCombo(index)}
                        className="hover:bg-[#EE4423]/20 rounded-full p-1 transition-colors"
                      >
                        <MdClose size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Colour - Last Field */}
            <div className="">
              <label className="block text-white text-sm font-medium mb-2">
                Theme Colour
              </label>
              <div className="flex gap-4 items-center">
                <input
                  type="color"
                  value={formData.themeColour}
                  onChange={(e) => setFormData({ ...formData, themeColour: e.target.value })}
                  className="h-12 w-20 rounded-lg cursor-pointer bg-[#212121] border border-gray-700"
                />
                <input
                  type="text"
                  value={formData.themeColour}
                  onChange={(e) => setFormData({ ...formData, themeColour: e.target.value })}
                  placeholder="#EE4423"
                  className="flex-1 px-4 py-3 bg-[#212121] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423] transition-colors"
                />
              </div>
            </div>
            </div>
          </div>

          {/* Right Column - Reference Images (30%) */}
          <div className="col-span-3">
            <div>
              <label className="block text-white text-sm font-medium mb-4">
                Knowledge
              </label>
              
              {/* Grid container for upload button and images - full width when no images */}
              <div className={`grid gap-3 ${formData.refImage.length === 0 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {/* Upload button - always first */}
                <button
                  type="button"
                  onClick={() => setShowMediaLibrary(true)}
                  className="aspect-square bg-[#212121] border-2 border-dashed border-gray-700 rounded-xl text-gray-400 hover:border-[#EE4423] hover:text-[#EE4423] transition-colors flex flex-col items-center justify-center gap-2"
                >
                  <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs text-center px-2">Upload or browse from Pexels</p>
                </button>

                {/* Uploaded images */}
                {formData.refImage.map((image, index) => (
                  <div key={index} className="relative group aspect-square">
                    <img
                      src={image}
                      alt={`Knowledge ${index + 1}`}
                      className="w-full h-full object-cover rounded-xl border-2 border-gray-700"
                    />
                    <button
                      type="button"
                      onClick={() => removeRefImage(index)}
                      className="absolute top-2 right-2 p-1.5 bg-black/80 rounded-full hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <MdClose size={16} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.form>

      {/* Media Library Sliding Panel */}
      <MediaLibrary
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelectMedia={handleSelectMedia}
        selectedImages={formData.refImage}
        onRemoveImage={removeRefImage}
      />
    </>
  );
};

export default CreateOwnForm;
